import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowCircleLeft, CaretLeft, CaretRight, ArrowClockwise, DownloadSimple, Translate, Rows } from "@phosphor-icons/react";

/**
 * Two-pane PDF comparer for the same report in EN and AR.
 *
 * Two design constraints drive the implementation:
 *
 * 1. **PDF cache warming.** The backend's `/reports/:id/pdf` endpoint
 *    renders on-demand when the cache is cold. That render can take
 *    30–90s for a full 70-page report and the endpoint returns a
 *    202 status while it's in flight. An `<iframe>` cannot handle a
 *    202, the plugin just shows blank. So before we point the
 *    iframes at anything, we pre-warm both PDFs via `pdfStatus` +
 *    a background `fetch()` that triggers the backend renderer.
 *
 * 2. **Page navigation without re-downloading.** Every major browser's
 *    built-in PDF viewer honours the `#page=N` fragment. To jump both
 *    panes to a new page without re-downloading the PDF, we mutate the
 *    `contentWindow.location.hash` on each iframe (same-origin, no
 *    CORS issue). We deliberately do NOT bump a query-string cache
 *    key here, that would force a full re-download and was the
 *    original source of the "repeated downloads + 504 timeout" bug.
 */
export default function ReportComparer() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────
  // Access gate: free-tier users must never reach the dual-pane
  // preview — the paid PDF renderer that this page depends on is
  // 402-gated for them, so both iframes would spin forever. Also,
  // per product spec the free tier does not get the Arabic edition
  // at all. Redirect them straight back to the Report page.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("esg_token");
    if (!token) { navigate("/login"); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/status`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((s) => {
        if (cancelled || !s) return;
        if (!s.active && !s.grandfathered) {
          navigate(`/report/${id}`, { replace: true });
        }
      })
      .catch(() => { /* fail-open, page will render with 402 iframes */ });
    return () => { cancelled = true; };
  }, [id, navigate]);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [synced, setSynced] = useState(true);
  const [translating, setTranslating] = useState(false);
  // Per-pane readiness. "checking" → "warming" (backend rendering) → "ready" (PDF cached).
  const [enState, setEnState] = useState({ status: "checking", waited: 0 });
  const [arState, setArState] = useState({ status: "checking", waited: 0 });
  // Bumped only by the explicit "Reload" button to force iframes to re-fetch.
  const [reloadKey, setReloadKey] = useState(0);
  // Layout mode, narrow (mobile portrait) shows one language at a time
  // with an EN/AR toggle; wide (tablet landscape + desktop) keeps the
  // classic side-by-side. We treat anything ≥ 768px as wide because
  // below that, two-pane comparison of prose becomes unreadable.
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false
  );
  // Which language pane is visible when in narrow mode.
  const [mobileLang, setMobileLang] = useState("en");

  const enFrameRef = useRef(null);
  const arFrameRef = useRef(null);

  // Reactively track the (max-width: 767px) breakpoint so layout mode
  // swaps cleanly on orientation change / window resize without a
  // full page reload.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setNarrow(e.matches);
    // Older Safari uses addListener; modern uses addEventListener.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    api.getReport(id)
      .then(setReport)
      .catch(() => navigate("/dashboard"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const hasEn = !!report?.has_en || (report?.language === "en");
  const hasAr = !!report?.has_ar || (report?.language === "ar");
  const bothReady = hasEn && hasAr;

  // ------------------------------------------------------------------
  // PDF cache warming. For each language: check status → if not ready,
  // kick a background fetch (which triggers the backend renderer + fills
  // the GridFS cache) → poll status until ready. Runs in parallel per
  // pane so the faster one starts rendering right away.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!bothReady) return;
    let cancelled = false;

    const warm = async (lang, setState) => {
      try {
        const s = await api.pdfStatus(id, lang);
        if (cancelled) return;
        if (s?.ready) {
          setState({ status: "ready", waited: 0 });
          return;
        }
        setState({ status: "warming", waited: 0 });
        // Kick the backend renderer by fetching the PDF endpoint. We
        // don't await the full body, a 202 comes back quickly telling us
        // the render kicked off, and the poll loop below then waits for
        // the cache to fill. We swallow errors: the poll loop is the
        // source of truth for readiness.
        const token = localStorage.getItem("esg_token") || "";
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${id}/pdf?lang=${lang}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: "include",
        }).catch(() => {});
        // Poll status every 3s, up to ~4 minutes.
        const startedAt = Date.now();
        for (let i = 0; i < 80; i++) {
          if (cancelled) return;
          await new Promise(r => setTimeout(r, 3000));
          const waited = Math.floor((Date.now() - startedAt) / 1000);
          try {
            const st = await api.pdfStatus(id, lang);
            if (cancelled) return;
            if (st?.ready) {
              setState({ status: "ready", waited });
              return;
            }
            setState({ status: "warming", waited });
          } catch {
            setState({ status: "warming", waited });
          }
        }
        if (!cancelled) setState({ status: "error", waited: 0 });
      } catch {
        if (!cancelled) setState({ status: "error", waited: 0 });
      }
    };

    setEnState({ status: "checking", waited: 0 });
    setArState({ status: "checking", waited: 0 });
    warm("en", setEnState);
    warm("ar", setArState);
    return () => { cancelled = true; };
  }, [id, bothReady, reloadKey]);

  const enUrl = report ? `${api.pdfUrl(id, "en", true)}#page=${page}` : "";
  const arUrl = report ? `${api.pdfUrl(id, "ar", true)}#page=${page}` : "";

  // ------------------------------------------------------------------
  // Page navigation: mutate the iframe's location.hash directly so the
  // browser's PDF viewer jumps without re-downloading the file.
  // ------------------------------------------------------------------
  const goToPage = useCallback((p) => {
    const clean = Math.max(1, Math.min(200, Number(p) || 1));
    setPage(clean);
    setPageInput(String(clean));
    const applyHash = (frame) => {
      if (!frame) return;
      try {
        // Same-origin iframe: safe to touch contentWindow.location.hash.
        // This is the ONLY reliable way to move the built-in PDF viewer
        // without a full reload. On narrow (mobile-portrait) layout
        // only ONE iframe is mounted at a time, the other ref is
        // null, so we bail via the guard above.
        frame.contentWindow.location.hash = `page=${clean}`;
      } catch {
        // Cross-origin fallback (shouldn't happen, API is same-origin as
        // the frontend, but we defend against exotic proxy configs).
        try { frame.src = frame.src.split("#")[0] + `#page=${clean}`; } catch { /* no-op */ }
      }
    };
    applyHash(enFrameRef.current);
    applyHash(arFrameRef.current);
  }, []);

  const handlePrev = () => goToPage(page - 1);
  const handleNext = () => goToPage(page + 1);
  const handleReload = () => setReloadKey(k => k + 1);

  const handleTranslate = async () => {
    const missing = hasEn ? "ar" : "en";
    const targetName = missing === "ar" ? "Arabic" : "English";
    if (!window.confirm(`Generate the ${targetName} translation? This may take a few minutes.`)) return;
    setTranslating(true);
    try {
      const r = await api.translateReport(id, missing);
      if (r.status !== "already_translated") {
        await api.pollUntilTranslated(id, () => {});
      }
      const updated = await api.getReport(id);
      setReport(updated);
      setReloadKey(k => k + 1);
    } catch (err) {
      alert("Translation failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setTranslating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070709]">
        <div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!report) return null;

  const companyName = report.company_data?.name || "Report";
  const bothWarmed = enState.status === "ready" && arState.status === "ready";

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#070709" }}>
      {/* Sticky header with sync controls */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070709]/85 border-b border-white/[0.06]">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate(`/report/${id}`)}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
              data-testid="compare-back-button"
            >
              <ArrowCircleLeft size={20} /> Back to report
            </button>
            <div className="hidden sm:block h-5 w-px bg-slate-700" />
            <div className="min-w-0">
              <div className="font-['Outfit'] text-[9px] font-bold tracking-[0.3em] uppercase text-[#01AF9B]">
                Bilingual comparison
              </div>
              <h1 className="font-['Outfit'] text-base sm:text-lg font-bold text-white truncate">
                {companyName}
              </h1>
            </div>
          </div>

          {bothReady && !narrow && (
            <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="compare-controls">
              {/* Sync toggle */}
              <button
                onClick={() => setSynced(s => !s)}
                className={`flex items-center gap-1.5 text-xs font-['Outfit'] font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                  synced
                    ? "bg-[#01AF9B]/15 text-[#01AF9B] border-[#01AF9B]/40"
                    : "text-slate-400 border-slate-700 hover:bg-slate-800"
                }`}
                data-testid="compare-sync-toggle"
                title="When ON, page navigation moves both panes together. When OFF, each pane scrolls independently."
              >
                <span className={`inline-block w-2 h-2 rounded-full ${synced ? "bg-[#01AF9B]" : "bg-slate-500"}`} />
                {synced ? "Synced pages" : "Independent"}
              </button>

              {/* Page navigation */}
              <div className="flex items-center gap-1 bg-[#0D1117] border border-slate-800 rounded-lg p-1">
                <button
                  onClick={handlePrev}
                  disabled={!synced || page <= 1 || !bothWarmed}
                  className="p-1.5 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded"
                  data-testid="compare-prev-page"
                  title="Previous page"
                >
                  <CaretLeft size={16} weight="bold" />
                </button>
                <span className="text-slate-500 text-xs mr-1">Page</span>
                <input
                  type="number"
                  min="1"
                  max="200"
                  value={pageInput}
                  onChange={e => setPageInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") goToPage(pageInput);
                  }}
                  onBlur={() => goToPage(pageInput)}
                  disabled={!synced || !bothWarmed}
                  className="w-14 bg-transparent text-white text-sm text-center font-['Outfit'] font-bold focus:outline-none disabled:opacity-40"
                  data-testid="compare-page-input"
                />
                <button
                  onClick={handleNext}
                  disabled={!synced || !bothWarmed}
                  className="p-1.5 text-slate-300 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed rounded"
                  data-testid="compare-next-page"
                  title="Next page"
                >
                  <CaretRight size={16} weight="bold" />
                </button>
              </div>

              <button
                onClick={handleReload}
                className="flex items-center gap-1.5 text-slate-400 hover:text-white text-xs font-['Outfit'] font-semibold border border-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-800 transition-colors"
                data-testid="compare-reload"
                title="Reload both PDFs"
              >
                <ArrowClockwise size={14} /> Reload
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Missing-translation state */}
      {!bothReady && (
        <div className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="max-w-lg text-center bg-gradient-to-br from-[#B94B05]/20 to-[#B94B05]/5 border border-[#B94B05]/40 rounded-2xl p-8" data-testid="compare-missing-translation">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#B94B05]/20 border border-[#B94B05]/40 flex items-center justify-center mb-4">
              <Translate size={28} className="text-[#B94B05]" weight="bold" />
            </div>
            <h2 className="font-['Outfit'] text-xl font-bold text-white mb-2">
              {hasEn ? "Arabic version not yet generated" : "English version not yet generated"}
            </h2>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              To compare English and Arabic side-by-side, both language versions of this
              report must be available. Generate the missing translation to continue.
            </p>
            <button
              onClick={handleTranslate}
              disabled={translating}
              className="bg-[#B94B05] hover:bg-[#EA580C] text-white font-['Outfit'] font-semibold text-sm py-2.5 px-5 rounded-lg transition-all inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="compare-translate-cta"
            >
              <Translate size={16} weight="bold" />
              {translating ? "Translating…" : `Generate ${hasEn ? "Arabic" : "English"} version`}
            </button>
          </div>
        </div>
      )}

      {/* Two-pane comparer.
          Wide layout (≥768px): both panes side-by-side, sync toggle
          controls cross-pane page nav.
          Narrow layout (<768px, mobile portrait): only ONE pane is
          mounted at a time, controlled by the EN/AR toggle in the
          header. This avoids the "two 180px unreadable strips" trap
          and works correctly on iOS Safari (which happily renders a
          single full-width PDF iframe, but often chokes on side-by-side
          panes in a flex-row). The page number persists across
          language switches via the URL #page fragment. */}
      {bothReady && !narrow && (
        <div className="flex-1 flex flex-col lg:flex-row" data-testid="compare-panes">
          <ComparePane
            lang="en"
            label="English"
            accent="#01AF9B"
            reportId={id}
            state={enState}
            frameRef={enFrameRef}
            url={enUrl}
            reloadKey={reloadKey}
            side="left"
          />
          <ComparePane
            lang="ar"
            label="Arabic · عربي"
            accent="#B94B05"
            reportId={id}
            state={arState}
            frameRef={arFrameRef}
            url={arUrl}
            reloadKey={reloadKey}
            side="right"
          />
        </div>
      )}
      {/* Mobile portrait, Compare view is unsupported.
          Mobile browsers cannot render PDFs inline in iframes (both
          Android Chrome and iOS Safari fall back to a "Download / Open"
          button that opens the PDF outside our app and logs the user
          out of the session context). Rather than ship a half-broken
          UX, we show a clear message telling the user to use a wider
          screen and give them direct-download buttons as the fallback. */}
      {bothReady && narrow && (
        <div
          className="flex-1 flex flex-col items-center justify-center px-6 py-12 text-center"
          data-testid="compare-mobile-unsupported"
        >
          <div className="w-14 h-14 rounded-full bg-[#01AF9B]/15 border border-[#01AF9B]/40 flex items-center justify-center mb-4">
            <Rows size={26} className="text-[#01AF9B]" weight="bold" />
          </div>
          <h2 className="font-['Outfit'] text-lg font-bold text-white mb-2">
            Compare view needs a wider screen
          </h2>
          <p className="text-slate-400 text-sm leading-relaxed max-w-xs mb-6">
            Mobile browsers can&apos;t render PDFs side-by-side. Open this
            page on a tablet or laptop to compare the English and Arabic
            versions live, or download each PDF below to review offline.
          </p>
          <div className="flex flex-col gap-2 w-full max-w-xs">
            <a
              href={api.pdfUrl(id, "en")}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold text-sm py-2.5 px-4 rounded-lg transition-all inline-flex items-center justify-center gap-2"
              data-testid="compare-mobile-download-en"
            >
              <DownloadSimple size={16} weight="bold" /> Download PDF (EN)
            </a>
            <a
              href={api.pdfUrl(id, "ar")}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="bg-[#B94B05] hover:bg-[#EA580C] text-white font-['Outfit'] font-semibold text-sm py-2.5 px-4 rounded-lg transition-all inline-flex items-center justify-center gap-2"
              data-testid="compare-mobile-download-ar"
            >
              <DownloadSimple size={16} weight="bold" /> تحميل PDF (عربي)
            </a>
            <button
              onClick={() => navigate(`/report/${id}`)}
              className="text-slate-400 hover:text-white text-xs font-['Outfit'] font-semibold py-2 mt-2"
              data-testid="compare-mobile-back"
            >
              ← Back to report
            </button>
          </div>
        </div>
      )}

      {/* Info footer, only meaningful in the wide side-by-side layout. */}
      {bothReady && !narrow && (
        <div className="border-t border-white/[0.06] bg-[#0B0C10] px-4 py-2 text-center text-slate-500 text-[11px]">
          Browser PDF viewers don&apos;t expose live scroll position, use <span className="text-[#01AF9B] font-semibold">Page ◄ ►</span> above to jump both panes together, or toggle to <span className="text-slate-300 font-semibold">Independent</span> to scroll each side freely.
        </div>
      )}
    </div>
  );
}

/**
 * One pane of the two-pane comparer. Shows a loading/warming state while
 * the backend renders the PDF, then swaps to the iframe once the cache
 * is warm. Never points the iframe at a URL that could return 202.
 */
function ComparePane({ lang, label, accent, reportId, state, frameRef, url, reloadKey, side, mobile = false }) {
  const isLeft = side === "left";
  const isReady = state.status === "ready";
  const isError = state.status === "error";
  // Layout: full viewport height when solo on mobile, otherwise the
  // classic half-viewport pane in the side-by-side layout.
  const heightClass = mobile
    ? "min-h-[calc(100vh-120px)]"
    : `min-h-[50vh] lg:min-h-0 border-b lg:border-b-0 ${isLeft ? "lg:border-r" : ""}`;
  return (
    <div className={`flex-1 flex flex-col ${heightClass} border-white/[0.06]`}>
      <div className="flex items-center justify-between px-4 py-2 bg-[#0D1117] border-b border-white/[0.06]">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-['Outfit'] font-bold tracking-[0.2em] uppercase px-2 py-0.5 rounded border"
            style={{ backgroundColor: `${accent}1a`, color: accent, borderColor: `${accent}4d` }}
          >
            {lang.toUpperCase()}
          </span>
          <span className="text-slate-400 text-xs">{label}</span>
          {!isReady && !isError && (
            <span className="ml-2 text-slate-500 text-[11px] inline-flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
              {state.status === "checking" ? "Checking cache…" : `Rendering… ${state.waited}s`}
            </span>
          )}
        </div>
        <a
          href={api.pdfUrl(reportId, lang)}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-slate-500 text-xs transition-colors hover:opacity-80"
          style={{ color: accent }}
          data-testid={`compare-download-${lang}`}
        >
          <DownloadSimple size={14} /> PDF
        </a>
      </div>

      {/* Body: loading state OR ready iframe. We only mount the <iframe>
          when the backend confirms the PDF cache is warm, that way the
          iframe never renders a 202 (blank/504 error page). */}
      {!isReady ? (
        <div className="flex-1 flex items-center justify-center bg-[#0B0C10] p-8" data-testid={`compare-pane-loading-${lang}`}>
          <div className="max-w-sm text-center">
            {isError ? (
              <>
                <div className="w-10 h-10 mx-auto rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center mb-3">
                  <span className="text-red-400 text-lg font-bold">!</span>
                </div>
                <p className="text-red-300 text-sm font-['Outfit'] font-semibold mb-1">PDF render taking too long</p>
                <p className="text-slate-500 text-xs">The backend hasn&apos;t returned the {label} PDF yet. Click <span className="text-white font-semibold">Reload</span> above to try again, or download it directly with the PDF link.</p>
              </>
            ) : (
              <>
                <div
                  className="w-10 h-10 mx-auto rounded-full border-2 border-t-transparent animate-spin mb-4"
                  style={{ borderColor: accent, borderTopColor: "transparent" }}
                />
                <p className="text-slate-200 text-sm font-['Outfit'] font-semibold mb-1">
                  Preparing {label} PDF
                </p>
                <p className="text-slate-500 text-xs">
                  {state.status === "checking"
                    ? "Checking if a cached copy exists…"
                    : `Rendering the ${label} PDF on the server (${state.waited}s). First open of a report takes 30–60 seconds; subsequent opens are instant.`}
                </p>
              </>
            )}
          </div>
        </div>
      ) : (
        <iframe
          ref={frameRef}
          key={`${lang}-${reloadKey}`}
          src={url}
          title={`${label} report`}
          className="flex-1 w-full bg-white border-0"
          data-testid={`compare-iframe-${lang}`}
        />
      )}
    </div>
  );
}
